/**
 * IHS-9 Shots — shots / on-target / off-target per game with toggle
 * between attack (shots taken) and defence (shots conceded).
 *
 * Layout (iteration 2, 2026-05-17):
 *   - Top row: [home crest] [home total/game] [crosshair icon]
 *              [away total/game] [away crest]
 *   - Composite row: home off-target circle | home on-target circle |
 *              goal-net SVG (with on-target counts overlaid INSIDE the
 *              net via SVG circles) | away on-target circle |
 *              away off-target circle
 *   - Below: centred mode label ("Shots / Game" or "Shots conceded").
 *
 * Conceded-tab-hidden behaviour preserved: when neither side reports
 * conceded data the toggle row is omitted and we render attack-only.
 */

import { useState, type ReactElement } from 'react';
import { useTranslation } from '../../../i18n/index.js';
import { useTenantConfig } from '../../../tenant/useTenantConfig.js';
import type { IhsShotsData, IhsShotsSide } from '../../../types/ihs.js';
import { formatDecimalAvg } from '../../../utils/numbers.js';
import { IhsCard, ihsCardStyles } from './IhsCard.js';
import { GoalNetSvg } from './IhsPrimitives.js';
import { TeamCrest } from '../../primitives/TeamCrest.js';
import { ValueBox } from '../../primitives/ValueBox.js';
import { TEAM_HOME_COLOR, TEAM_AWAY_COLOR } from '../../../styles/team-colors.js';
import styles from './IhsShots.module.css';

const HOME_COLOR = TEAM_HOME_COLOR;
const AWAY_COLOR = TEAM_AWAY_COLOR;

function shotsBucket(
  side: IhsShotsSide,
  mode: 'attack' | 'defense',
): {
  total: number | null;
  onTarget: number | null;
  offTarget: number | null;
} {
  if (mode === 'attack') {
    return {
      total: side.shotsPerGame,
      onTarget: side.shotsOnTargetPerGame,
      offTarget: side.shotsOffTargetPerGame,
    };
  }
  return {
    total: side.shotsConcededPerGame ?? null,
    onTarget: side.shotsOnTargetConcededPerGame ?? null,
    offTarget: null,
  };
}

/**
 * CircleStat — thin local adapter around `<ValueBox>` (Phase 0b
 * extraction). Preserves the same prop signature so the call sites
 * stay compact while sharing the primitive's solid/outline logic.
 */
function CircleStat({
  value,
  color,
  variant,
  label,
  decimalSeparator,
}: {
  value: number | null;
  color: string;
  /** outline = light-fill outlined circle. solid = saturated fill. */
  variant: 'outline' | 'solid';
  label: string;
  decimalSeparator: '.' | ',';
}): ReactElement {
  return (
    <ValueBox
      value={formatDecimalAvg(value, decimalSeparator)}
      label={label}
      color={color}
      shape="circle"
      size="md"
      variant={variant}
      labelPlace="below"
    />
  );
}

export function IhsShots({ data }: { data: IhsShotsData }): ReactElement {
  const hasConcededData =
    (data.home.shotsConcededPerGame ?? null) !== null ||
    (data.away.shotsConcededPerGame ?? null) !== null;
  const [mode, setMode] = useState<'attack' | 'defense'>('attack');
  const { t } = useTranslation();
  const tenant = useTenantConfig();
  const decimalSeparator =
    tenant.kind === 'live' || tenant.kind === 'paused'
      ? tenant.config.market.decimalSeparator
      : '.';

  const title =
    data.header?.title ?? t('ihs.shots.title', { defaultValue: 'Shots' });

  const bar = hasConcededData ? (
    <div className={ihsCardStyles.toggleRow} role="tablist">
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'attack'}
        className={`${ihsCardStyles.toggleBtn}${mode === 'attack' ? ` ${ihsCardStyles.toggleBtnActive}` : ''}`}
        onClick={(): void => setMode('attack')}
      >
        {t('ihs.shots.onAndOff', { defaultValue: 'On & Off / Game' })}
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'defense'}
        className={`${ihsCardStyles.toggleBtn}${mode === 'defense' ? ` ${ihsCardStyles.toggleBtnActive}` : ''}`}
        onClick={(): void => setMode('defense')}
      >
        {t('ihs.shots.conceded', { defaultValue: 'Conceded / Game' })}
      </button>
    </div>
  ) : null;

  const effectiveMode = hasConcededData ? mode : 'attack';
  const homeBucket = shotsBucket(data.home, effectiveMode);
  const awayBucket = shotsBucket(data.away, effectiveMode);

  const onTargetLabel = t('ihs.shots.onTarget', { defaultValue: 'On' });
  const offTargetLabel = t('ihs.shots.offTarget', { defaultValue: 'Off' });
  const totalLabel =
    effectiveMode === 'attack'
      ? t('ihs.shots.shotsPerMatch', { defaultValue: 'Shots / Game' })
      : t('ihs.shots.shotsConceded', { defaultValue: 'Shots conceded' });

  const homeOnTargetStr = formatDecimalAvg(homeBucket.onTarget, decimalSeparator);
  const awayOnTargetStr = formatDecimalAvg(awayBucket.onTarget, decimalSeparator);

  return (
    <IhsCard title={title} {...(bar ? { bar } : {})}>
      {/* Top row: crest | total/game | crosshair | total/game | crest */}
      <div className={styles.topRow}>
        <TeamCrest
          {...(data.home.team.badge !== undefined ? { src: data.home.team.badge } : {})}
          name={data.home.team.name}
          size={40}
        />
        <span className={styles.topTotal}>
          {formatDecimalAvg(homeBucket.total, decimalSeparator)}
        </span>
        <CrosshairIcon />
        <span className={styles.topTotal}>
          {formatDecimalAvg(awayBucket.total, decimalSeparator)}
        </span>
        <TeamCrest
          {...(data.away.team.badge !== undefined ? { src: data.away.team.badge } : {})}
          name={data.away.team.name}
          size={40}
        />
      </div>

      {/* Composite row: circles | goal-net (with on-target counts inside) | circles */}
      <div className={styles.composite}>
        {/* Home pair (off-target outermost, on-target nearest the net) */}
        <div className={styles.pair}>
          <CircleStat
            value={homeBucket.offTarget}
            color={HOME_COLOR}
            variant="outline"
            label={offTargetLabel}
            decimalSeparator={decimalSeparator}
          />
          <CircleStat
            value={homeBucket.onTarget}
            color={HOME_COLOR}
            variant="solid"
            label={onTargetLabel}
            decimalSeparator={decimalSeparator}
          />
        </div>

        {/* Centre: goal-net with on-target counts overlaid INSIDE the net */}
        <div className={styles.center}>
          <GoalNetSvg
            width={104}
            homeOnTarget={homeBucket.onTarget !== null ? homeOnTargetStr : null}
            awayOnTarget={awayBucket.onTarget !== null ? awayOnTargetStr : null}
            homeColor={HOME_COLOR}
            awayColor={AWAY_COLOR}
          />
        </div>

        {/* Away pair (on-target nearest the net, off-target outermost) */}
        <div className={styles.pair}>
          <CircleStat
            value={awayBucket.onTarget}
            color={AWAY_COLOR}
            variant="solid"
            label={onTargetLabel}
            decimalSeparator={decimalSeparator}
          />
          <CircleStat
            value={awayBucket.offTarget}
            color={AWAY_COLOR}
            variant="outline"
            label={offTargetLabel}
            decimalSeparator={decimalSeparator}
          />
        </div>
      </div>
      <div className={styles.centerLabel}>{totalLabel}</div>
    </IhsCard>
  );
}

/**
 * Small target-style icon used between the two total/game numbers on the
 * top row. A neutral grey crosshair: outer ring + dot in centre. Inline
 * SVG to avoid an extra asset for a 14px glyph.
 */
function CrosshairIcon(): ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      aria-hidden="true"
      className={styles.crosshair}
    >
      <circle cx="8" cy="8" r="7" fill="none" stroke="#A0A0A0" strokeWidth="1.5" />
      <circle cx="8" cy="8" r="1.6" fill="#A0A0A0" />
    </svg>
  );
}
